import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link2, PlayCircle, AlertTriangle, CheckCircle2, HelpCircle, XCircle, Download } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/kaizen";
import { StripeFullAudit } from "../components/stripe/StripeFullAudit";
import { StripeDuplicatesAudit } from "../components/stripe/StripeDuplicatesAudit";

export default function StripeReconcileModule() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [webhook, setWebhook] = useState(null);

  useEffect(() => {
    api.get("/stripe/lifecycle/log").then(({ data }) => setWebhook(data)).catch(() => {});
  }, []);

  const downloadAudit = () => {
    if (!lastBatch) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("map_token");
    fetch(`${base}/api/stripe-reconcile/audit/${lastBatch}.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit_stripe_${lastBatch.slice(0, 8)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Téléchargement du journal impossible"));
  };

  const runDryRun = async () => {
    setLoading(true);
    setReport(null);
    try {
      const { data } = await api.get("/stripe-reconcile/dry-run", { timeout: 120000 });
      setReport(data);
      toast.success(`Simulation terminée · ${data.subscriptions_found} abonnement(s) Stripe analysé(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la simulation");
    } finally {
      setLoading(false);
    }
  };

  const applyMatches = async () => {
    if (!report || report.totals.matched === 0) return;
    if (!window.confirm(`Appliquer le rapprochement pour ${report.totals.matched} contrat(s) certains ? Les cas ambigus (${report.totals.ambiguous}) ne seront PAS touchés.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/stripe-reconcile/apply?confirm=true", {}, { timeout: 120000 });
      toast.success(`✅ ${data.updated} contrat(s) reliés à Stripe (mode ${data.stripe_mode})`);
      if (data.batch_id) setLastBatch(data.batch_id);
      runDryRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'application");
    } finally {
      setApplying(false);
    }
  };

  const T = report?.totals;

  return (
    <div className="max-w-5xl mx-auto" data-testid="stripe-reconcile-module">
      <PageHeader
        icon={Link2}
        title="Rapprochement Stripe → Contrats"
        subtitle="Relie chaque abonnement Stripe au bon contrat (courriel → téléphone → nom) et calcule la prochaine échéance. Simulation d'abord — rien n'est écrit tant que vous ne validez pas."
        actions={
          <button data-testid="dry-run-btn" onClick={runDryRun} disabled={loading}
            className="bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-50 text-white font-medium px-5 py-2.5 flex items-center gap-2 transition-colors">
            <PlayCircle size={18} /> {loading ? "Analyse…" : "Lancer la simulation"}
          </button>
        }
      />

      {webhook && (
        <div className="mb-6 border border-white/10 bg-black/30 p-4" data-testid="webhook-info">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm text-white/80 font-medium">⚙️ Automatisation (webhook Stripe)</p>
              <p className="text-xs text-white/50 mt-1">Après paiement, le contrat + l'échéance se mettent à jour automatiquement.</p>
            </div>
            <span className={`text-xs uppercase tracking-wider border px-3 py-1.5 ${webhook.signature_configured ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>
              {webhook.signature_configured ? "Signature configurée" : "Signature à configurer"}
            </span>
          </div>
          <div className="mt-3 text-xs text-white/60 space-y-1">
            <p>URL à enregistrer dans Stripe : <code className="text-white/80 bg-white/10 px-1.5 py-0.5">{`https://<votre-domaine>${webhook.webhook_url_suffix}`}</code></p>
            <p>Événements traités : {webhook.counts.events} · Événements sans contrat (à revoir) : <b className={webhook.counts.unmatched ? "text-amber-400" : "text-white/60"}>{webhook.counts.unmatched}</b></p>
            {!webhook.signature_configured && <p className="text-amber-400/80">⚠️ En production : ajoutez la variable STRIPE_WEBHOOK_SECRET (fournie par Stripe lors de la création du webhook).</p>}
          </div>
        </div>
      )}

      {report?.warning_test_mode && (
        <div className="mb-6 border border-amber-500/50 bg-amber-500/10 p-4 text-sm text-amber-300" data-testid="test-mode-warning">
          {report.warning_test_mode}
        </div>
      )}

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="reconcile-totaux">
            {[
              { l: "Matchs certains", v: T.matched, icon: CheckCircle2, color: "text-emerald-400", accent: false },
              { l: "Ambigus", v: T.ambiguous, icon: HelpCircle, color: "text-amber-400" },
              { l: "Stripe sans contrat", v: T.stripe_without_contract, icon: XCircle, color: "text-[#D90429]" },
              { l: "Contrats actifs sans abo.", v: T.active_contracts_without_sub, icon: AlertTriangle, color: "text-white/70" },
            ].map((s) => (
              <div key={s.l} className="border border-white/10 bg-black/30 p-4">
                <div className="flex items-center gap-2 text-white/50"><s.icon size={14} /><p className="text-xs uppercase tracking-wider">{s.l}</p></div>
                <p className={`text-3xl font-semibold mt-1 ${s.color}`}>{s.v}</p>
              </div>
            ))}
          </div>

          <div className="mb-6 flex items-center gap-3 flex-wrap">
            <span className={`text-xs uppercase tracking-wider border px-3 py-1.5 ${report.stripe_mode === "live" ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>
              Stripe : mode {report.stripe_mode}
            </span>
            <button data-testid="apply-btn" onClick={applyMatches} disabled={applying || T.matched === 0}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-medium px-5 py-2.5 flex items-center gap-2 transition-colors">
              <CheckCircle2 size={18} /> {applying ? "Application…" : `Appliquer les ${T.matched} matchs certains`}
            </button>
            {lastBatch && (
              <button data-testid="download-audit-btn" onClick={downloadAudit}
                className="border border-white/15 hover:border-emerald-500 px-4 py-2.5 flex items-center gap-2 text-sm transition-colors">
                <Download size={16} /> Télécharger le journal CSV
              </button>
            )}
          </div>

          <SectionCard title={`Matchs certains (${T.matched})`} className="mb-4">
            {report.matched.length === 0 ? <p className="text-white/50 text-sm">Aucun match.</p> : (
              <div className="space-y-1.5" data-testid="matched-list">
                {report.matched.slice(0, 40).map((m, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-1.5">
                    <span className="text-white/80 truncate">{m.contract_name} <span className="text-white/40">({m.stripe_email})</span></span>
                    <span className="text-white/50 text-xs shrink-0">par {m.match_by} · échéance {m.ancienne_echeance ? <span className="text-white/40 line-through">{m.ancienne_echeance}</span> : <span className="text-white/30">(vide)</span>} → <b className="text-emerald-400">{m.next_due || "?"}</b></span>
                  </div>
                ))}
                {report.matched.length > 40 && <p className="text-xs text-white/40 pt-1">… et {report.matched.length - 40} autre(s).</p>}
              </div>
            )}
          </SectionCard>

          {T.ambiguous > 0 && (
            <SectionCard title={`Cas ambigus — validation manuelle requise (${T.ambiguous})`} className="mb-4">
              <div className="space-y-1.5" data-testid="ambiguous-list">
                {report.ambiguous.slice(0, 30).map((a, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-1.5">
                    <span className="text-amber-300 truncate">{a.stripe_email || a.stripe_name}</span>
                    <span className="text-white/50 text-xs shrink-0">{a.contrats.length} contrats possibles · par {a.match_by}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {T.stripe_without_contract > 0 && (
            <SectionCard title={`Abonnements Stripe sans contrat Kaizen (${T.stripe_without_contract})`}>
              <div className="space-y-1.5" data-testid="no-contract-list">
                {report.stripe_without_contract.slice(0, 30).map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-1.5">
                    <span className="text-white/80 truncate">{s.stripe_name || "—"} <span className="text-white/40">({s.stripe_email})</span></span>
                    <span className="text-white/50 text-xs shrink-0">{s.status} · échéance {s.next_due || "?"}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </>
      )}

      {!report && !loading && (
        <div className="text-center py-16 text-white/40" data-testid="reconcile-empty">
          <Link2 size={40} className="mx-auto mb-3 opacity-40" />
          <p>Cliquez sur « Lancer la simulation » pour comparer Stripe et vos contrats.</p>
          <p className="text-xs mt-1">Aucune donnée ne sera modifiée.</p>
        </div>
      )}

      <StripeFullAudit />
      <StripeDuplicatesAudit />
    </div>
  );
}
